/**
 * Commercial Quote Builder — domain types (Enterprise Sprint 3.0).
 * Pure TypeScript. No React, no I/O, no Prisma.
 */

export const COMMERCIAL_ITEM_TYPES = [
  'Computer',
  'Notebook',
  'Printer',
  'Monitor',
  'Peripheral',
  'Software',
  'Service',
  'License',
] as const

export type CommercialItemType = (typeof COMMERCIAL_ITEM_TYPES)[number]

/** Hardware slots that compose a Computer (never a flat SKU). */
export const COMPUTER_SLOTS = [
  'cpu',
  'motherboard',
  'ram',
  'storage',
  'gpu',
  'psu',
  'case',
  'cooler',
  'other',
] as const

export type ComputerSlot = (typeof COMPUTER_SLOTS)[number]

/** Slots required to finalize a Computer configuration. */
export const COMPUTER_REQUIRED_SLOTS: readonly ComputerSlot[] = [
  'cpu',
  'motherboard',
  'ram',
  'storage',
  'psu',
  'case',
] as const

export type CommercialOriginSource = 'catalog' | 'template' | 'builder' | 'manual'

export interface CommercialOrigin {
  source: CommercialOriginSource
  catalogId?: string
  templateId?: string
  fingerprint?: string
}

/**
 * One part inside a composite CommercialItem (Computer BOM).
 * Simple items (Monitor, Service, …) do not use components.
 */
export interface CommercialComponent {
  slot: ComputerSlot
  catalogId: string
  label: string
  brand: string
  model: string
  quantity: number
  unitCost: number
  unitPrice: number
}

/**
 * Official commercial unit for the Quote Builder.
 * Compatible with Budget Engine via CommercialBudgetService.
 *
 * Computer = configuration (components[]), not a single product SKU.
 */
export interface CommercialItem {
  id: string
  type: CommercialItemType
  name: string
  description: string
  quantity: number
  /** Aggregated cost (sum of components × qty for Computer). */
  unitCost: number
  /** Aggregated sale price. */
  unitPrice: number
  /** Present when type === 'Computer' (and optionally Notebook kits). */
  components?: CommercialComponent[]
  origin: CommercialOrigin
  meta?: Record<string, string>
}

/** Catalog entry used to pick parts / finished goods quickly. */
export interface CommercialCatalogEntry {
  id: string
  type: CommercialItemType | 'Component'
  /** For Component entries. */
  slot?: ComputerSlot
  label: string
  brand: string
  model: string
  description: string
  unitCost: number
  unitPrice: number
  tags: string[]
  active: boolean
}

export interface CommercialTemplateDefinition {
  id: string
  name: string
  description: string
  /** Target CommercialItem type produced by the template. */
  itemType: CommercialItemType
  /** For Computer: catalog ids per slot. */
  componentCatalogIds?: Partial<Record<ComputerSlot, string>>
  /** For simple items: single catalog id. */
  catalogId?: string
  /** Optional display name override. */
  itemName?: string
  tags: string[]
}

export type ComputerBuildMode = 'summary' | 'expanded'

export interface CommitCommercialOptions {
  /**
   * summary — one Budget line for the whole Computer
   * expanded — title/note + one line per component
   */
  mode?: ComputerBuildMode
}

export interface ComputerBuilderState {
  name: string
  description: string
  quantity: number
  slots: Partial<Record<ComputerSlot, CommercialComponent>>
}

export interface ComputerValidationIssue {
  severity: 'error' | 'warning'
  code: string
  message: string
  slot?: ComputerSlot
}

export interface ComputerValidationResult {
  valid: boolean
  issues: ComputerValidationIssue[]
}
